import { useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import './AuthPages.css';

const emptyForm = { name:'', email:'', password:'', role:'employee', phone:'', department:'' };

export default function RegisterPage() {
  const { register, user: currentUser, logout, updateUser } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [removingId, setRemovingId] = useState('');
  const [updatingRoleId, setUpdatingRoleId] = useState('');
  const [editingUser, setEditingUser] = useState(null);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await api.get('/users', { params: { isActive: true } });
      setUsers(res.data.data?.users || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load users.');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await register(form);
      setMessage('User created successfully.');
      setForm(emptyForm);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'User creation failed.');
    } finally {
      setLoading(false);
    }
  };

  const removeUser = async (user) => {
    const isSelf = user._id === currentUser?._id;
    const confirmText = isSelf
      ? `Remove your own account (${user.name})? You will be signed out immediately.`
      : `Remove ${user.name} from active users?`;

    if (!window.confirm(confirmText)) return;

    setError('');
    setMessage('');
    setRemovingId(user._id);
    try {
      await api.delete(`/users/${user._id}`);
      if (isSelf) {
        logout();
        return;
      }
      setMessage('User removed successfully.');
      setEditingUser(null);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'User removal failed.');
    } finally {
      setRemovingId('');
    }
  };

  const changeRole = async (user, role) => {
    if (role === user.role) return;

    const isSelf = user._id === currentUser?._id;
    const confirmText = isSelf && role !== 'admin'
      ? `Change your own role to ${role}? You will leave the admin user management screen.`
      : `Change ${user.name}'s role to ${role}?`;

    if (!window.confirm(confirmText)) return;

    setError('');
    setMessage('');
    setUpdatingRoleId(user._id);
    try {
      const res = await api.put(`/users/${user._id}`, { role });
      const updated = res.data.data?.user;
      setUsers((list) => list.map((item) => (item._id === user._id ? updated || { ...item, role } : item)));
      setEditingUser(updated || { ...user, role });
      if (isSelf && updated) updateUser(updated);
      setMessage('Role updated successfully.');
    } catch (err) {
      setError(err.response?.data?.message || 'Role update failed.');
    } finally {
      setUpdatingRoleId('');
    }
  };

  return (
    <div className="slide-up users-admin-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1>Users</h1>
          <p>Only admins can add users, remove users, and manage roles.</p>
        </div>
      </div>

      <div className="users-admin-grid">
      <div className="card">
        <div className="auth-form-box slide-up" style={{ boxShadow:'none', maxWidth:'none', padding:0 }}>
          <div className="auth-form-header">
            <h2 className="auth-form-title">New team member</h2>
            <p className="auth-form-sub">Create login access for an employee, manager, or admin.</p>
          </div>

          {error && <div className="alert alert-error">Warning: {error}</div>}
          {message && <div className="alert alert-success">{message}</div>}

          <form onSubmit={submit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" name="name" placeholder="Arjun Sharma"
                  value={form.name} onChange={handle} required />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" name="phone" placeholder="+91 98765 43210"
                  value={form.phone} onChange={handle} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" name="email" placeholder="arjun@tatsavispaces.com"
                value={form.email} onChange={handle} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" name="role" value={form.role} onChange={handle}>
                  <option value="employee">Employee</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <input className="form-input" name="department" placeholder="Design / Execution"
                  value={form.department} onChange={handle} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Temporary Password</label>
              <input className="form-input" type="password" name="password" placeholder="Min 6 characters"
                value={form.password} onChange={handle} required minLength={6} autoComplete="new-password" />
            </div>
            <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={loading}
              style={{ marginTop: '0.5rem' }}>
              {loading ? <><span className="spinner" /> Creating user...</> : 'Create User'}
            </button>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="users-card-header">
          <div>
            <h2 className="card-title users-card-title">Active users</h2>
            <p className="auth-form-sub">Manage active accounts and role access.</p>
          </div>
          <span className="stat-label">{users.length} active</span>
        </div>

        {usersLoading ? (
          <div className="users-loading">
            <span className="spinner" />
            Loading users...
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state users-empty">
            <h3>No active users</h3>
            <p>Create a team member to see them here.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u._id === currentUser?._id;
                  return (
                    <tr key={u._id}>
                      <td>
                        <div className="user-name-cell">
                          <strong>{u.name}</strong>
                          <span>{u.email}</span>
                        </div>
                      </td>
                      <td><span className={`badge badge-${u.role}`}>{u.role}</span></td>
                      <td>{u.department || '-'}</td>
                      <td>
                        <span className="badge badge-active">
                          {isSelf ? 'Current user' : 'Active'}
                        </span>
                      </td>
                      <td>
                        <div className="user-actions">
                          <button className="btn btn-secondary btn-sm" type="button" onClick={() => setEditingUser(u)}>
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>

      {editingUser && (
        <div className="modal-overlay" role="presentation" onClick={() => setEditingUser(null)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="edit-user-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title" id="edit-user-title">Edit user</h2>
              <button className="modal-close" type="button" onClick={() => setEditingUser(null)} aria-label="Close">
                x
              </button>
            </div>
            <div className="modal-body">
              <div className="edit-user-summary">
                <strong>{editingUser.name}</strong>
                <span>{editingUser.email}</span>
              </div>

              <div className="form-group">
                <label className="form-label">Manage Role</label>
                <select
                  className={`form-select role-select role-select-${editingUser.role}`}
                  value={editingUser.role}
                  onChange={(e) => changeRole(editingUser, e.target.value)}
                  disabled={updatingRoleId === editingUser._id}
                >
                  <option value="employee">Employee</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" type="button" onClick={() => setEditingUser(null)}>
                Close
              </button>
              <button
                className="btn btn-danger"
                type="button"
                onClick={() => removeUser(editingUser)}
                disabled={removingId === editingUser._id}
              >
                {removingId === editingUser._id ? 'Removing...' : 'Remove User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
